import currency from 'currency.js';
import { KpiCalculationResultType } from 'constants/kpis';

const PRIMITIVE_TYPES = [
  'boolean',
  'undefined',
  'number',
  'string',
  'bigint',
  'symbol',
];

export const removeByIndex = (arr, at) => arr.filter((item, idx) => idx !== at);

// creates object by deep merging array of objects
export function arrToObj(arr) {
  return arr.reduce(merge, {});
}

// Merge a `source` object to a `target` recursively
const merge = (target, source) => {
  // Iterate through `source` properties and if an `Object` set property
  // to merge of `target` and `source` properties
  for (let key of Object.keys(source)) {
    if (source[key] instanceof Object) {
      if (!(key in target)) target[key] = {};
      Object.assign(source[key], merge(target[key], source[key]));
    }
  }
  // Join `target` and modified `source`
  Object.assign(target || {}, source);
  return target;
};

/*
 * Joins multiple arrays index by index
 * [[1, 2], [3, 4], [5, 6]] => [[1, 3, 5], [2, 4, 6]]
 */
export function zip(arrays) {
  return arrays[0].map((v, i) => arrays.map((arr) => arr[i]));
}

export function removeProperty(obj, property) {
  return Object.keys(obj).reduce((acc, key) => {
    if (key !== (Number.isInteger(property) ? property.toString() : property)) {
      return { ...acc, [key]: obj[key] };
    }
    return acc;
  }, {});
}

export function isDeepEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  // if primitive values were equal it would already return true in the first line of this function
  if (PRIMITIVE_TYPES.includes(typeof a)) {
    return false;
  }

  if (Array.isArray(a)) {
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i <= a.length; i++) {
      if (!isDeepEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }

  return isDeepEqual(Object.entries(a).sort(), Object.entries(b).sort());
}

/*
 * Filter object properties by predicate function
 */
export function filterProperties(obj, predicate) {
  return Object.entries(obj)
    .filter(([key, val]) => predicate(key, val))
    .reduce((out, [key, val]) => {
      out[key] = val;
      return out;
    }, {});
}

export function randomInt(hi, lo = 0) {
  return Math.floor(Math.random() * (hi - lo)) + lo;
}

export function randomString(len, digits = true, upper = true, lower = true) {
  let chars = '';
  if (digits) chars += '0123456789';
  if (upper) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXTZ';
  if (lower) chars += 'abcdefghiklmnopqrstuvwxyz';
  return [...Array(len)]
    .map(() => chars[Math.floor(Math.random() * chars.length)])
    .join('');
}

export function currencySum(values) {
  return values.reduce((t, s) => t.add(s), currency(0));
}

export function roundInteger({ num, precision, place }) {
  const sign = Math.sign(num);
  const absoluteNum = Math.abs(num);
  const digits = Array.from(String(absoluteNum), Number);
  const digitsLength = digits.length;

  if (precision + place > digitsLength) {
    return 0;
  }

  const lastIndex = digitsLength - 1;
  const roundingDigitIndex = lastIndex - precision - place;
  const rightDigitIndex = roundingDigitIndex + 1;

  if (rightDigitIndex > lastIndex) {
    return num;
  }

  const rightDigit = digits[rightDigitIndex];
  const increment = rightDigit >= 5 ? 1 : 0;

  const firstPartNumber =
    parseInt(digits.slice(0, rightDigitIndex).join('') || '0', 10) + increment;
  const firstPart = firstPartNumber.toString();
  const zeroesCount = digitsLength - rightDigitIndex;
  const secondPart = zeroesCount > 0 ? '0'.repeat(zeroesCount) : '';

  const numStr = [...firstPart, ...secondPart];

  const roundedNumStr = numStr
    .slice(0, numStr.length - precision - place)
    .join('');
  const roundedNum = parseInt(roundedNumStr, 10) * sign;
  return roundedNum;
}

export const exportCellFormatter = (settings) => (params) => {
  const { value } = params;
  const hasIntValue = !!(value && value.intValue);
  if (hasIntValue) {
    const { groupsPrecision } = settings;
    const customFormatter = currencyValueFormatter(
      {
        groupsPrecision,
        thousandsSeparator: '',
        decimalSeparator: '.',
      },
      true
    );
    const formattedValue = customFormatter(params);
    return formattedValue;
  }
  return value;
};

export const copyCellFormatter = (settings) => (params) => {
  const { value } = params;
  const hasIntValue = value && Number.isFinite(value.intValue);
  if (hasIntValue) {
    const { groupsPrecision } = settings;
    const customFormatter = currencyValueFormatter(
      {
        groupsPrecision,
        thousandsSeparator: '',
        decimalSeparator: ',',
      },
      true
    );
    const formattedValue = customFormatter(params);
    return formattedValue;
  }
  return value;
};

export const currencyValueFormatter = (
  settings,
  useGroupsPrecision = false
) => (params) => {
  if (params.value === undefined) {
    return '';
  }

  let currValue = params.value;

  const groupsPrecision = parseInt(settings.groupsPrecision || '-1', 10);
  const hasGroupPrecision =
    useGroupsPrecision &&
    Number.isFinite(groupsPrecision) &&
    groupsPrecision !== -1;

  const hasIntValue = Number.isFinite(currValue.intValue);

  const shouldFormatWithGroupsPrecision = hasGroupPrecision && hasIntValue;

  let formatterConfig = {
    separator: settings.thousandsSeparator,
    decimal: settings.decimalSeparator,
  };

  if (shouldFormatWithGroupsPrecision) {
    const currencyPrecision = parseInt(currValue?.s?.precision || '2');
    formatterConfig = { ...formatterConfig, precision: 0 };
    currValue = roundInteger({
      num: currValue.intValue,
      precision: currencyPrecision,
      place: groupsPrecision,
    });
  }

  const curr = currency(currValue, formatterConfig);
  return curr.format();
};

export function numberParser(params) {
  let newValue = Number(params.newValue);
  return isNaN(newValue) ? params.oldValue : newValue;
}

export const copyCellKpiFormatter = (settings) => (params) => {
  const { value } = params;
  const isKpiValue = value?.type;

  if (isKpiValue) {
    const customFormatter = kpiValueFormatter({
      thousandsSeparator: '',
      decimalSeparator: ',',
      precision: settings.precision,
    });
    const formattedValue = customFormatter(params);
    return formattedValue;
  }
  return value;
};

export const kpiValueFormatter = (settings) => (params) => {
  if (params.data?.group) {
    return '';
  }

  const normalConfig = {
    precision: settings.precision,
  };
  const formatterConfig = {
    separator: settings.thousandsSeparator,
    decimal: settings.decimalSeparator,
    precision: settings.precision,
  };

  if (params.value && params.value.type) {
    switch (params.value.type) {
      case KpiCalculationResultType.result:
        // toFixed guarantees that number will be returned as
        // a string in a normal notation
        const kpiResultString = params.value.result.toFixed();
        // Currency is used solely for its formatting capabilities
        const kpiResultCurrency = currency(kpiResultString, normalConfig);
        return currency(kpiResultCurrency, formatterConfig).format();
      case KpiCalculationResultType.error:
        return params.value.status;
      default:
        return '';
    }
  }

  return '';
};

export function filenamify(name) {
  return name
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '');
}

function getRootToNodePath(nodeId, parentByNodeId) {
  const stack = [];
  const path = [];
  let currentId = nodeId;

  while (currentId !== null) {
    stack.push(currentId);
    currentId = parentByNodeId[currentId];
  }

  while (stack.length !== 0) {
    currentId = stack.pop();
    path.push(currentId);
  }

  return path;
}

export function getAllRootToNodePaths(rootId, nodesById) {
  const paths = [];
  const elementStack = [rootId];

  // Store id of the parent for every node in tree
  const parentIdByNodeId = {
    [rootId]: null,
  };

  while (elementStack.length !== 0) {
    let currentId = elementStack.pop();

    paths.push(getRootToNodePath(currentId, parentIdByNodeId));

    nodesById[currentId].childrenIds.forEach((childrenId) => {
      parentIdByNodeId[childrenId] = currentId;
      elementStack.push(childrenId);
    });
  }

  return paths;
}

// get all duplicate values from an array
// works with JS primitive values
export function getArrayDuplicateValues(data) {
  // get all duplicate values
  return data.filter((el, i, arr) => arr.indexOf(el) !== i);
}
